import type {
  AdminDomainPricingVersionSummary,
  PricingDomain,
  PublicPricingConfigResponse,
  PricingDomainConfigMap,
} from '@nebalkon-workspace/cabinet-core';
import type { PricingPromocodeEntry } from '@nebalkon-workspace/cabinet-core';
import {
  computeStorageMarketingMaxDiscountPercent,
  todayMinskIsoDate,
} from '@nebalkon-workspace/cabinet-core';
import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const PRICING_DOMAINS: readonly PricingDomain[] = [
  'storage',
  'moving',
  'promocodes',
] as const;

export const MOVING_RATE_FIELDS = [
  'packingByndPerM3',
  'unpackingByndPerM3',
  'carryOutByndPerPersonTripFloor',
  'carryInByndPerPersonTripFloor',
  'loadingByndPerPerson',
  'unloadingByndPerPerson',
  'transportMinByndPerTrip',
  'transportByndPerM3',
] as const;

export const FURNITURE_COMPLEXITIES = [
  'verySimple',
  'simple',
  'basic',
  'complex',
  'veryComplex',
] as const;

/** NotInput type="number" emits strings — coerce before admin pricing API calls. */
export function coercePricingNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/\s+/g, '').replace(',', '.');
    if (trimmed === '' || trimmed === '.' || trimmed === '-') {
      return fallback;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

export function hasValidStorageServiceRates(
  rates: PricingDomainConfigMap['storage']['serviceRates'] | undefined,
): boolean {
  if (!rates) {
    return false;
  }

  return (
    coercePricingNumber(rates.pickupByndPerM3) > 0 &&
    coercePricingNumber(rates.returnByndPerM3) > 0 &&
    coercePricingNumber(rates.packagingByndPerM3) > 0 &&
    coercePricingNumber(rates.pickupMinVolumeM3) > 0 &&
    coercePricingNumber(rates.returnMinVolumeM3) > 0 &&
    coercePricingNumber(rates.packagingMinVolumeM3) > 0
  );
}

/** Fills missing or zero serviceRates from merged baseline when editing legacy versions. */
export function mergeStorageServiceRatesForForm(
  config: PricingDomainConfigMap['storage'],
  fallback: PricingDomainConfigMap['storage'],
): PricingDomainConfigMap['storage'] {
  if (hasValidStorageServiceRates(config.serviceRates)) {
    return config;
  }

  return {
    ...config,
    serviceRates: { ...fallback.serviceRates },
  };
}

function isEmptyPricingInput(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

/** Min validator for decimal pricing fields (supports comma and dot). */
export function pricingMin(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmptyPricingInput(control.value)) {
      return null;
    }

    const parsed = coercePricingNumber(control.value, Number.NaN);
    if (!Number.isFinite(parsed)) {
      return { pricingNumber: true };
    }

    return parsed >= min ? null : { min: { min, actual: parsed } };
  };
}

/** Max validator for decimal pricing fields (supports comma and dot). */
export function pricingMax(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmptyPricingInput(control.value)) {
      return null;
    }

    const parsed = coercePricingNumber(control.value, Number.NaN);
    if (!Number.isFinite(parsed)) {
      return { pricingNumber: true };
    }

    return parsed <= max ? null : { max: { max, actual: parsed } };
  };
}

function coerceFurnitureRates<T extends Record<(typeof FURNITURE_COMPLEXITIES)[number], number>>(
  source: Partial<Record<(typeof FURNITURE_COMPLEXITIES)[number], unknown>> | T,
  fallback: T,
): T {
  return Object.fromEntries(
    FURNITURE_COMPLEXITIES.map((key) => [
      key,
      coercePricingNumber(source?.[key], fallback[key] ?? 0),
    ]),
  ) as T;
}

/** Ensures domain config JSON matches Go float64/int expectations (no string numbers). */
export function normalizeDomainConfigForApi<D extends PricingDomain>(
  domain: D,
  config: PricingDomainConfigMap[D],
): PricingDomainConfigMap[D] {
  switch (domain) {
    case 'storage': {
      const storage = config as PricingDomainConfigMap['storage'];
      const volumeDiscountTiers = (storage.volumeDiscountTiers ?? []).map(
        (tier) => ({
          minVolumeM3: coercePricingNumber(tier.minVolumeM3),
          discountPercent: coercePricingNumber(tier.discountPercent),
        }),
      );
      const maxPromoDiscountPercent = coercePricingNumber(
        storage.maxPromoDiscountPercent,
      );

      return {
        baseRateByndPerM3Day: coercePricingNumber(storage.baseRateByndPerM3Day),
        minVolumeM3: coercePricingNumber(storage.minVolumeM3),
        maxAutoStandardDiscountPercent: computeStorageMarketingMaxDiscountPercent(
          volumeDiscountTiers,
          maxPromoDiscountPercent,
        ),
        maxPromoDiscountPercent,
        volumeDiscountTiers,
        serviceRates: {
          pickupByndPerM3: coercePricingNumber(
            storage.serviceRates?.pickupByndPerM3,
          ),
          returnByndPerM3: coercePricingNumber(
            storage.serviceRates?.returnByndPerM3,
          ),
          packagingByndPerM3: coercePricingNumber(
            storage.serviceRates?.packagingByndPerM3,
          ),
          pickupMinVolumeM3: coercePricingNumber(
            storage.serviceRates?.pickupMinVolumeM3,
          ),
          returnMinVolumeM3: coercePricingNumber(
            storage.serviceRates?.returnMinVolumeM3,
          ),
          packagingMinVolumeM3: coercePricingNumber(
            storage.serviceRates?.packagingMinVolumeM3,
          ),
        },
      } as PricingDomainConfigMap[D];
    }
    case 'moving': {
      const moving = config as PricingDomainConfigMap['moving'];
      const rates = moving.rates;
      return {
        rates: {
          disassemblyByndPerPiece: coerceFurnitureRates(
            rates.disassemblyByndPerPiece,
            rates.disassemblyByndPerPiece,
          ),
          assemblyByndPerPiece: coerceFurnitureRates(
            rates.assemblyByndPerPiece,
            rates.assemblyByndPerPiece,
          ),
          packingByndPerM3: coercePricingNumber(rates.packingByndPerM3),
          unpackingByndPerM3: coercePricingNumber(rates.unpackingByndPerM3),
          carryOutByndPerPersonTripFloor: coercePricingNumber(
            rates.carryOutByndPerPersonTripFloor,
          ),
          carryInByndPerPersonTripFloor: coercePricingNumber(
            rates.carryInByndPerPersonTripFloor,
          ),
          loadingByndPerPerson: coercePricingNumber(rates.loadingByndPerPerson),
          unloadingByndPerPerson: coercePricingNumber(
            rates.unloadingByndPerPerson,
          ),
          transportMinByndPerTrip: coercePricingNumber(
            rates.transportMinByndPerTrip,
          ),
          transportByndPerM3: coercePricingNumber(rates.transportByndPerM3),
        },
      } as PricingDomainConfigMap[D];
    }
    case 'promocodes': {
      const promocodes = config as PricingDomainConfigMap['promocodes'];
      return {
        promocodes: (promocodes.promocodes ?? [])
          .filter((entry) => entry.promocode?.trim())
          .map((entry) => {
            const normalized: PricingPromocodeEntry = {
              promocode: entry.promocode.trim().toUpperCase(),
              discount: coercePricingNumber(entry.discount),
            };
            const commissionRaw = entry.commission;
            if (commissionRaw != null && String(commissionRaw).trim() !== '') {
              normalized.commission = coercePricingNumber(commissionRaw);
            }
            return normalized;
          }),
      } as PricingDomainConfigMap[D];
    }
  }
}

export function isPricingDomain(value: string | null | undefined): value is PricingDomain {
  return PRICING_DOMAINS.includes(value as PricingDomain);
}

export function extractDomainConfig<D extends PricingDomain>(
  domain: D,
  merged: PublicPricingConfigResponse,
): PricingDomainConfigMap[D] {
  switch (domain) {
    case 'storage':
      return merged.storage as PricingDomainConfigMap[D];
    case 'moving':
      return { rates: merged.moving.rates } as PricingDomainConfigMap[D];
    case 'promocodes':
      return {
        promocodes: merged.promocodes ?? [],
      } as PricingDomainConfigMap[D];
  }
}

export function formatPricingDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatPricingRate(rate: number | undefined): string {
  if (rate == null || Number.isNaN(rate)) {
    return '—';
  }
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(rate);
}

export function isPricingVersionCurrent(
  version: AdminDomainPricingVersionSummary,
): boolean {
  const today = todayMinskIsoDate();
  const from = version.effective_from;
  const to = version.effective_to;
  if (from > today) {
    return false;
  }
  if (!to) {
    return true;
  }
  return to >= today;
}

export function isPricingVersionFuture(
  version: AdminDomainPricingVersionSummary,
): boolean {
  return version.effective_from > todayMinskIsoDate();
}

export interface UnifiedPricingTimelineEntry {
  domain: PricingDomain;
  version: AdminDomainPricingVersionSummary;
}

export function buildUnifiedPricingTimeline(
  versionsByDomain: Partial<
    Record<PricingDomain, AdminDomainPricingVersionSummary[]>
  >,
): UnifiedPricingTimelineEntry[] {
  const entries: UnifiedPricingTimelineEntry[] = [];
  for (const domain of PRICING_DOMAINS) {
    for (const version of versionsByDomain[domain] ?? []) {
      entries.push({ domain, version });
    }
  }
  return entries.sort(
    (a, b) =>
      new Date(a.version.effective_from).getTime() -
      new Date(b.version.effective_from).getTime(),
  );
}
